/**
 * Size guide panel (blocks/size-guide.liquid).
 * - Any [data-size-guide-open] button on the page opens it (the variant picker's "Size guide").
 * - The column of the size currently picked in the variant picker is highlighted.
 */
class SizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('click', this.#onClick);
    this.dialog?.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-size-guide-open]') : null;
    if (!opener || !this.dialog) return;
    event.preventDefault();
    this.#highlightSelected();
    this.#selectTab('chart');
    this.dialog.showModal();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-size-guide-close]')) this.dialog?.close();
    const unitButton = target.closest('[data-unit]');
    if (unitButton instanceof HTMLElement) this.#setUnit(unitButton.dataset.unit ?? 'cm');
    const tab = target.closest('[data-size-guide-tab]');
    if (tab instanceof HTMLElement) this.#selectTab(tab.dataset.sizeGuideTab ?? 'chart');
  };

  /** Switches between the size chart and how to measure */
  /** @param {string} name */
  #selectTab(name) {
    for (const tab of this.querySelectorAll('[data-size-guide-tab]')) {
      const selected = tab.getAttribute('data-size-guide-tab') === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const panel of this.querySelectorAll('[data-size-guide-panel]')) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-size-guide-panel') !== name);
    }
  }

  /** Clicking the dimmed backdrop (the dialog element itself, outside its content) closes it */
  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (!this.dialog || event.target !== this.dialog) return;
    const rect = this.dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.dialog.close();
  };

  /** @param {string} unit */
  #setUnit(unit) {
    for (const button of this.querySelectorAll('[data-unit]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-unit') === unit));
    }
    for (const cell of this.querySelectorAll('td[data-cm]')) {
      const cm = parseFloat(cell.getAttribute('data-cm') ?? '');
      if (Number.isNaN(cm)) continue;
      cell.textContent = unit === 'in' ? String(Math.round((cm / 2.54) * 10) / 10) : String(cell.getAttribute('data-cm'));
    }
  }

  #highlightSelected() {
    // Comma list: the setting plus the translated names (Size / Kích cỡ…)
    const optionNames = (this.dataset.optionName ?? 'size')
      .toLowerCase()
      .split(',')
      .map((name) => name.trim());
    let selected = '';
    for (const input of document.querySelectorAll('variant-picker input[type="radio"]:checked')) {
      if (optionNames.includes((input.getAttribute('data-option-name') ?? '').toLowerCase())) {
        selected = /** @type {HTMLInputElement} */ (input).value;
      }
    }
    // Sizes are columns: every cell of the picked size gets the highlight
    for (const cell of this.querySelectorAll('[data-size]')) {
      cell.classList.toggle('is-selected', selected !== '' && cell.getAttribute('data-size') === selected);
    }
  }
}

if (!customElements.get('size-guide')) customElements.define('size-guide', SizeGuide);
